import {
  Network,
  Search,
  Link2,
  FileText,
  Key,
  Palette,
  PlayCircle,
  BookOpen,
  Lock,
  Globe,
  Braces,
  Regex,
  Send,
  KeyRound,
  Sparkles,
  Database,
  Wrench,
  Gauge,
  ShieldCheck,
  Code2,
  Clock as ClockIcon,
  Fingerprint,
  Hash,
  Workflow,
  Wand2,
  Shrink,
  Image as ImageIcon,
  Package,
  ScanEye,
  Brain,
  FileCode,
  Calculator,
  type LucideIcon,
} from "lucide-react";

export type Category =
  | "data"
  | "api"
  | "frontend"
  | "performance"
  | "security"
  | "productivity"
  | "everyday";

export interface Tool {
  icon: LucideIcon;
  title: string;
  description: string;
  features: string[];
  href: string;
  external: boolean;
  category: Category;
  label?: string; // NEW, BETA, AI, INVITE ONLY
  featured?: boolean;
}

export const tools: Tool[] = [
  // Security & Privacy
  {
    icon: Key,
    title: "Password Generator",
    description:
      "Secure, memorable passwords following NIST guidelines. Choose length, symbols, characters — or generate strong multi-word passphrases. Runs entirely in your browser.",
    features: ["Cryptographically secure", "Customizable options", "Passphrase mode", "Zero logging"],
    href: "/tools/password-generator",
    external: false,
    category: "security",
    featured: true,
  },
  {
    icon: FileText,
    title: "Secure Note",
    description:
      "Share passwords, logins or texts, end-to-end encrypted, on one-time links that disappear after reading. No traces, no data.",
    features: ["End-to-end encrypted", "Zero logs", "Open and destroy", "Auto-expiration"],
    href: "/tools/secure-note",
    external: false,
    category: "security",
    featured: true,
  },
  // API & Network
  {
    icon: Network,
    title: "Network Toolkit",
    description:
      "Everything you need to debug, manage, and assess your domains, APIs, SSL, DNS records and more — all in one place.",
    features: ["DNS Lookup", "IP lookup", "SSL Certificate Info", "WHOIS Lookup", "HTTP Header Analyzer"],
    href: "/tools/network-toolkit",
    external: false,
    category: "api",
    featured: true,
  },
  {
    icon: Send,
    title: "HTTP Request Builder",
    description:
      "Lightweight REST client in your browser. Build requests, add headers, view responses, and copy as cURL. Postman alternative.",
    features: ["All HTTP methods", "Custom headers + body", "Response timing + status", "Copy as cURL"],
    href: "/tools/http-request-builder",
    external: false,
    category: "api",
    label: "NEW",
  },
  {
    icon: Code2,
    title: "cURL → Code Converter",
    description:
      "Paste a cURL command and instantly get equivalent code in JavaScript, Node.js, Python, PHP, and Go.",
    features: ["JS fetch + axios", "Python requests", "PHP cURL + Go", "Live conversion"],
    href: "/tools/curl-converter",
    external: false,
    category: "api",
    label: "NEW",
  },
  {
    icon: Database,
    title: "Fake API Generator",
    description:
      "Generate realistic mock JSON with custom fields and a matching JSON Schema. Perfect for prototyping APIs and UIs.",
    features: ["20+ field types", "Up to 500 records", "Matching JSON Schema", "Download or copy"],
    href: "/tools/fake-api-generator",
    external: false,
    category: "api",
    label: "NEW",
  },
  {
    icon: Workflow,
    title: "GraphQL Explorer",
    description:
      "Run GraphQL queries with variables and headers, and introspect any GraphQL schema. GraphiQL-style, in your browser.",
    features: ["Query + variables", "Custom headers", "Schema introspection", "JSON response viewer"],
    href: "/tools/graphql-explorer",
    external: false,
    category: "api",
    label: "NEW",
  },
  // Performance & SEO
  {
    icon: Search,
    title: "SEO Analyzer",
    description:
      "All-in-one SEO analysis and audit suite that takes 30 seconds. Page, keyword, mobile-friendly, search metrics, and competitor research — for free.",
    features: ["On-Page SEO Analyzer", "Meta tag analyzer", "Core Web Vitals report"],
    href: "/tools/seo-analyzer",
    external: false,
    category: "performance",
    featured: true,
  },
  {
    icon: Gauge,
    title: "Core Web Vitals Checker",
    description:
      "Single-URL deep dive into LCP, CLS, INP and more — real Lighthouse data on mobile and desktop.",
    features: ["LCP, CLS, INP, FCP, TBT", "Mobile + desktop", "Top opportunities", "Real Lighthouse data"],
    href: "/tools/core-web-vitals",
    external: false,
    category: "performance",
    label: "NEW",
  },
  {
    icon: Gauge,
    title: "Lighthouse Quick Audit",
    description: "All four Lighthouse scores plus ranked optimization opportunities for any URL.",
    features: [
      "Performance + A11y + SEO + Best Practices",
      "Ranked opportunities",
      "Mobile + desktop",
      "Real Lighthouse data",
    ],
    href: "/tools/lighthouse-audit",
    external: false,
    category: "performance",
    label: "NEW",
  },
  {
    icon: ImageIcon,
    title: "Image Optimizer",
    description:
      "Compress and convert images to WebP, AVIF, JPEG or PNG. Resize and batch process — all in your browser.",
    features: ["WebP + AVIF + JPEG + PNG", "Quality + resize", "Batch processing", "Up to 80% smaller"],
    href: "/tools/image-optimizer",
    external: false,
    category: "performance",
    label: "NEW",
  },
  {
    icon: Wand2,
    title: "JS/CSS Minifier & Beautifier",
    description:
      "Beautify or minify JavaScript, CSS, HTML and JSON — powered by Prettier and Terser, in your browser.",
    features: ["Prettier beautify", "Terser minify", "JS, CSS, HTML, JSON", "Live byte-savings"],
    href: "/tools/code-formatter",
    external: false,
    category: "performance",
    label: "NEW",
  },
  {
    icon: Package,
    title: "Bundle Size Analyzer",
    description:
      "Upload a JS bundle and see raw, minified, gzip and brotli sizes — plus the top modules eating your budget.",
    features: ["Real gzip + brotli sizes", "Top modules detected", "Smart warnings", "Composition breakdown"],
    href: "/tools/bundle-analyzer",
    external: false,
    category: "performance",
    label: "NEW",
  },
  // Frontend & Design
  {
    icon: Palette,
    title: "Color Palette Generator",
    description:
      "Generate stunning, accessible color schemes in hexcode for Tailwind CSS, compatible with websites, products, UI/UX or social posts/emails.",
    features: ["Accessibility grade", "Multi-color themes", "Multiple formats", "Dark mode sets", "Contrast preview"],
    href: "/tools/color-palette-generator",
    external: false,
    category: "frontend",
  },
  {
    icon: Sparkles,
    title: "CSS Generator Suite",
    description:
      "Visually craft box-shadows, border-radius, gradients, glassmorphism and neumorphism with live previews. Copy production-ready CSS in one click.",
    features: ["Box shadow + radius", "Linear / radial / conic gradients", "Glassmorphism + Neumorphism", "Live preview"],
    href: "/tools/css-generator",
    external: false,
    category: "frontend",
    label: "NEW",
  },
  {
    icon: Wand2,
    title: "Tailwind Class Builder",
    description:
      "Visually compose Tailwind utilities — layout, spacing, color, typography and effects — and copy ready-to-paste classes or JSX.",
    features: ["Every utility group", "Live preview", "Copy as classes or JSX", "v3 compatible"],
    href: "/tools/tailwind-builder",
    external: false,
    category: "frontend",
    label: "NEW",
  },
  {
    icon: Shrink,
    title: "SVG Optimizer & Converter",
    description:
      "Shrink SVG files in your browser and export as React JSX, base64 data URI, or CSS background image.",
    features: ["Live byte-savings", "Strip metadata + IDs", "React JSX export", "Data URI + CSS"],
    href: "/tools/svg-optimizer",
    external: false,
    category: "frontend",
    label: "NEW",
  },
  {
    icon: ImageIcon,
    title: "Favicon Generator",
    description:
      "Turn any image or emoji into a complete favicon set — every size, multi-size .ico, Apple touch icon and HTML.",
    features: ["From image or emoji", "9 sizes + multi-size .ico", "Apple touch + PWA", "Copy-paste HTML"],
    href: "/tools/favicon-generator",
    external: false,
    category: "frontend",
    label: "NEW",
  },
  // Data & Formatters
  {
    icon: Braces,
    title: "JSON Formatter & Validator",
    description:
      "Beautify, minify and validate JSON instantly. Catches syntax errors with line and position. Runs entirely in your browser.",
    features: ["Beautify with 2-space indent", "Minify for production", "Inline syntax validation", "Zero uploads"],
    href: "/tools/json-formatter",
    external: false,
    category: "data",
    label: "NEW",
  },
  {
    icon: KeyRound,
    title: "JWT Decoder",
    description:
      "Inspect JSON Web Tokens — decode header, payload, and claims with expiry detection. Decoding only, never sent to a server.",
    features: ["Decode header + payload", "Expiry & nbf detection", "Readable timestamps", "Browser-only"],
    href: "/tools/jwt-decoder",
    external: false,
    category: "data",
    label: "NEW",
  },
  {
    icon: Code2,
    title: "Base64 & URL Encoder",
    description:
      "Encode and decode Base64, Base64URL, and URL-encoded text instantly. Edit either side and watch the other update.",
    features: ["Base64 + Base64URL", "URL encoding", "Two-way live conversion", "Unicode safe"],
    href: "/tools/encoder-decoder",
    external: false,
    category: "data",
    label: "NEW",
  },
  {
    icon: ClockIcon,
    title: "Unix Timestamp Converter",
    description:
      "Convert Unix timestamps to readable dates and back. Seconds, milliseconds, ISO 8601, UTC and local time.",
    features: ["Live current Unix time", "Seconds + milliseconds", "ISO 8601 + UTC + local", "Relative time"],
    href: "/tools/timestamp-converter",
    external: false,
    category: "data",
    label: "NEW",
  },
  {
    icon: Fingerprint,
    title: "UUID Generator & Validator",
    description: "Generate cryptographically random UUIDv4 in bulk, or validate any UUID against RFC 4122.",
    features: ["UUIDv4 (cryptorandom)", "Bulk generate (1–50)", "RFC 4122 validation", "Version + variant"],
    href: "/tools/uuid-generator",
    external: false,
    category: "data",
    label: "NEW",
  },
  {
    icon: Hash,
    title: "Hash Generator",
    description:
      "Generate MD5, SHA-1, SHA-256, SHA-384, SHA-512, and bcrypt hashes — and verify bcrypt — in your browser.",
    features: ["MD5, SHA-1/256/384/512", "bcrypt with cost factor", "Verify bcrypt hashes", "Zero uploads"],
    href: "/tools/hash-generator",
    external: false,
    category: "security",
    label: "NEW",
  },
  {
    icon: Gauge,
    title: "Password Strength Tester",
    description:
      "Find out how strong your password really is — powered by zxcvbn. Entropy, crack-time estimates and improvement tips, all in your browser.",
    features: ["zxcvbn-powered score", "Real crack-time estimate", "Entropy in bits", "Plain-English tips"],
    href: "/tools/password-strength-tester",
    external: false,
    category: "security",
    label: "NEW",
  },
  {
    icon: KeyRound,
    title: "JWT Debugger",
    description:
      "Decode JSON Web Tokens and verify HS256/HS384/HS512 signatures with your secret. Inspects claims, expiry, and headers.",
    features: ["Decode header + payload", "Verify HMAC signatures", "Expiry & nbf detection", "Browser-only"],
    href: "/tools/jwt-debugger",
    external: false,
    category: "security",
    label: "NEW",
  },
  {
    icon: Hash,
    title: "HMAC Generator",
    description:
      "Sign messages with HMAC-SHA1/256/384/512 — perfect for verifying webhook signatures from Stripe, GitHub, and Slack.",
    features: ["SHA-1/256/384/512", "Text, hex or base64 keys", "Hex + Base64 + Base64URL", "WebCrypto local"],
    href: "/tools/hmac-generator",
    external: false,
    category: "security",
    label: "NEW",
  },
  {
    icon: ShieldCheck,
    title: "SSL Checker",
    description:
      "Check any domain's SSL certificate — issuer, expiry, TLS version and a quick A–F health grade. Simple SSL Labs alternative.",
    features: ["A–F health grade", "Days until expiration", "Issuer + SAN entries", "TLS protocol + cipher"],
    href: "/tools/ssl-checker",
    external: false,
    category: "security",
    label: "NEW",
  },
  {
    icon: ScanEye,
    title: "IP & Headers Inspector",
    description:
      "See your public IP, ISP, geolocation and every HTTP header your browser actually sends. Useful for debugging APIs, VPNs, and fingerprinting.",
    features: ["Public IP detection", "Geolocation + ISP", "All request headers", "Refresh on VPN switch"],
    href: "/tools/ip-inspector",
    external: false,
    category: "api",
    label: "NEW",
  },
  // Productivity
  {
    icon: Regex,
    title: "Regex Tester",
    description:
      "Test JavaScript regular expressions in real time with live highlighting, every flag, and capture-group inspection.",
    features: ["Live match highlighting", "All flags (g, i, m, s, u)", "Capture group details", "Browser-only"],
    href: "/tools/regex-tester",
    external: false,
    category: "productivity",
    label: "NEW",
  },
  {
    icon: ClockIcon,
    title: "Cron Expression Builder",
    description:
      "Decode and build cron schedules with plain-English explanations and the next 5 run times — like crontab.guru, in your browser.",
    features: ["Plain-English decoding", "Next 5 run times", "Common presets", "Live validation"],
    href: "/tools/cron-builder",
    external: false,
    category: "productivity",
    label: "NEW",
  },
  {
    icon: FileText,
    title: ".env Formatter",
    description:
      "Parse, validate and reformat .env files. Catches invalid keys, duplicates, unquoted values, and risky secrets — all in your browser.",
    features: ["dotenv-compatible parser", "Auto-quotes values", "Catches duplicates", "Never uploaded"],
    href: "/tools/env-formatter",
    external: false,
    category: "productivity",
    label: "NEW",
  },
  {
    icon: Package,
    title: "Dockerfile Linter",
    description:
      "Catch Dockerfile mistakes before they ship — pinned tags, COPY vs ADD, layer caches, root users and more. Hadolint-inspired.",
    features: ["Hadolint-style rules", "Severity-tagged findings", "Best-practice checks", "Browser-only"],
    href: "/tools/dockerfile-linter",
    external: false,
    category: "productivity",
    label: "NEW",
  },
  {
    icon: FileText,
    title: "Markdown → HTML Previewer",
    description:
      "Live GitHub-flavored Markdown editor with safe HTML output, copy and download. Sanitized via DOMPurify.",
    features: ["GitHub-flavored Markdown", "Live preview", "XSS-safe HTML output", "Copy or download"],
    href: "/tools/markdown-preview",
    external: false,
    category: "productivity",
    label: "NEW",
  },
  {
    icon: Code2,
    title: "Diff Checker",
    description:
      "Compare text, code or JSON — line, word, or structural — with unified and side-by-side views. Browser-only.",
    features: ["Line, word, JSON modes", "Unified + split view", "JSON key-order safe", "Zero uploads"],
    href: "/tools/diff-checker",
    external: false,
    category: "productivity",
    label: "NEW",
  },
  {
    icon: Link2,
    title: "URL Shortener",
    description:
      "Shorten long URLs quickly with vanity links to a clean, fast, unique site with trackable QR codes. Fast and easy.",
    features: ["Shorten any URL", "Auto generate short link", "View click data", "Track stats"],
    href: "https://neti.us",
    external: true,
    category: "productivity",
    label: "INVITE ONLY",
  },
  {
    icon: ClockIcon,
    title: "Online Clock",
    description:
      "World clock, stopwatch, countdown timer and alarm — all in one clean, distraction-free page that works in any browser.",
    features: ["World clock", "Stopwatch + timer", "Alarms", "Runs anywhere"],
    href: "https://clock.netius.app",
    external: true,
    category: "everyday",
  },
  {
    icon: Calculator,
    title: "Calculator",
    description:
      "Fast in-browser calculator for everyday math — keyboard friendly, with history and standard plus scientific modes.",
    features: ["Standard + scientific", "Keyboard friendly", "Calculation history", "Zero install"],
    href: "https://calc.netius.app",
    external: true,
    category: "everyday",
  },
  // AI-powered tools
  {
    icon: Brain,
    title: "AI Code Explainer",
    description:
      "Paste any code snippet and get a plain-English summary, step-by-step walkthrough, and likely bugs or improvements.",
    features: ["Any language", "Step-by-step", "Bug & smell hints", "Nothing stored"],
    href: "/tools/code-explainer",
    external: false,
    category: "data",
    label: "AI",
  },
  {
    icon: Regex,
    title: "Regex Generator",
    description:
      "Describe a pattern in plain English. Get a working regex with flags, an explanation, and matching/non-matching examples.",
    features: ["Plain-English in", "JavaScript flavor", "Examples included", "Nothing stored"],
    href: "/tools/regex-generator",
    external: false,
    category: "data",
    label: "AI",
  },
  {
    icon: Database,
    title: "SQL Query Builder",
    description:
      "Describe what you want from your database. Get clean SQL with explanation and assumptions, in the dialect you choose.",
    features: ["Postgres, MySQL & more", "Schema-aware", "Self-explaining", "Nothing stored"],
    href: "/tools/sql-builder",
    external: false,
    category: "data",
    label: "AI",
  },
  {
    icon: FileCode,
    title: "Test Data Generator",
    description:
      "Build a custom schema and generate realistic fake data — names, emails, addresses, dates, prices and more. Export as JSON, CSV or SQL.",
    features: ["Custom schema", "JSON · CSV · SQL", "Seeded reproducibility", "Runs in your browser"],
    href: "/tools/test-data-generator",
    external: false,
    category: "data",
    label: "NEW",
  },
  {
    icon: Search,
    title: "SEO Meta Generator",
    description:
      "Describe your page in a sentence. Get a complete SEO bundle — title, description, slug, H1, social tags and JSON-LD.",
    features: ["SERP-safe limits", "OG & Twitter tags", "JSON-LD included", "Nothing stored"],
    href: "/tools/seo-meta-generator",
    external: false,
    category: "performance",
    label: "AI",
  },
  {
    icon: PlayCircle,
    title: "YouTube Creator's Tool",
    description:
      "Analyze views, discover hidden tags, see channel stats and boost your YouTube, drive your business, and supercharge your growth.",
    features: ["Video performance trends", "Tag analysis + discovery", "Channel competitor stats", "Trend analysis"],
    href: "https://youtubecreatorstool.com",
    external: true,
    category: "productivity",
    label: "BETA",
  },
  {
    icon: BookOpen,
    title: "Amazon Book Tools",
    description:
      "Optimize your Kindle books, edit metadata, check titles, descriptions, keywords and more using A+ sales boosting tools.",
    features: ["KDP search/metadata result", "Title + descriptor analysis", "Category stats and suggestions"],
    href: "https://bt.netius.com",
    external: true,
    category: "productivity",
    label: "BETA",
  },
];

export const categories: { id: Category; label: string; icon: LucideIcon; intro: string }[] = [
  { id: "data", label: "Data & Formatters", icon: Database, intro: "Parse, format, decode and inspect data — JSON, JWT, and more." },
  { id: "api", label: "API & Network", icon: Network, intro: "Build, debug and inspect HTTP requests, DNS, and network details." },
  { id: "frontend", label: "Frontend & Design", icon: Palette, intro: "CSS, color, and UI helpers for designers and frontend developers." },
  { id: "performance", label: "Performance & SEO", icon: Gauge, intro: "Audit, analyze, and improve page speed and search performance." },
  { id: "security", label: "Security & Privacy", icon: ShieldCheck, intro: "Privacy-first generators and encryption tools that never leave your browser." },
  { id: "productivity", label: "Productivity", icon: Wrench, intro: "Quick utilities and external tools that speed up everyday workflows." },
  { id: "everyday", label: "Everyday Utilities", icon: Calculator, intro: "Simple, everyday browser tools — clock, calculator and other quick helpers anyone can use." },
];

export const labelStyles: Record<string, string> = {
  NEW: "bg-primary/20 text-primary",
  BETA: "bg-primary/20 text-primary",
  AI: "bg-primary/20 text-primary",
  "INVITE ONLY": "bg-muted text-muted-foreground border border-border",
};

export const comingSoonTools = [
  "GraphQL Schema Diff",
  "OpenAPI Validator",
  "YAML ↔ JSON",
  "Color Contrast Checker",
];

// Re-export shared icons used by Tools page hero/CTAs
export { Lock, Globe, ClockIcon as Clock };